import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { formatCents } from '@/shared/format/money';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { SPI_CONSULT_PATH, SPI_PATH } from './spi.constants';
import { SPI_APPLIED_KEYS, SPI_FILTER_KEYS, type SpiOperationFilters } from './spi.wire';

// Manifesto das duas telas da Gestão SPI (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K.
// Toda ação herda `FrontWebSPI:Read` da tela (`router.tsx:669-683`): o legado não tem gate próprio, nem
// Update — nem para o Aporte e o Saque, que mexem com dinheiro (⚠️ S1, pergunta para o Leon).

export { SPI_CONSULT_PATH, SPI_PATH };

/** `FrontWebSPI:Read` (`router.tsx:673`), a única permissão do recurso no legado. */
export const SPI_READ = can('FrontWebSPI', 'Read');
const READ = SPI_READ;

// ---------------------------------------------------------------------------------------------
// Painel · /contas-digitais/gestao-spi
// ---------------------------------------------------------------------------------------------

export const spiRefresh = action({
  id: 'spi.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo o saldo da conta corrente e o resumo da conta PI do painel SPI. Nada é alterado.`,
  permission: READ,
  // A tela troca este `run` pelo `refetch` das duas fontes (`SPIManagementContainer.tsx:61-65`).
  run: () => {
    throw new Error('spi.refresh needs the open SPI panel');
  },
});

// "Mostrar/ocultar valores" (`ShowValuesButton`) **não** é ação de manifesto: é estado de apresentação
// da tela, sem efeito e sem valor para o agente, e vira um botão no `aside` do cabeçalho.

export const spiOpenConsult = action({
  id: 'spi.openConsult',
  kind: 'navigate',
  label: msg`Consultas`,
  description: msg`Abre a tela de consulta de operações PIX da Gestão SPI.`,
  permission: READ,
  run: ({ nav }) => nav.to(SPI_CONSULT_PATH),
});

/**
 * Valor em centavos, como o `CurrencyFormField` do legado grava (`CurrencyFormField.tsx:100-108`).
 * Opcional: o cartão da tela abre o formulário vazio, e o agente pode preparar já com o valor.
 */
export const spiTransactionInput = z.strictObject({
  amountCents: z
    .number()
    .int()
    .min(1)
    .optional()
    .meta({ description: 'Amount in cents (BRL), a positive integer: 1500 means R$ 15,00. Leave it out to open the empty form.' }),
});
export type SpiTransactionInput = z.infer<typeof spiTransactionInput>;

const amountText = (input: SpiTransactionInput) => (input.amountCents === undefined ? undefined : formatCents(input.amountCents, { locale: i18n.locale }));

/**
 * Aporte: move dinheiro da conta corrente para a conta PI ("APORTE CC>>GI",
 * `TransactionActionDrawerForm.tsx:16`). Step-up (F2a): o `run` só abre o formulário já preenchido, e
 * quem confirma com senha + código MFA é a pessoa (`SPIManagementRequestContainer.tsx:83-100`).
 */
export const spiContribution = action({
  id: 'spi.contribution',
  kind: 'write',
  stepUp: true,
  label: msg`Aporte`,
  description: msg`Prepara um aporte, que leva dinheiro da conta corrente para a conta PI do sistema de liquidação SPI (CC>>GI). Abre o formulário com o valor preenchido; depois a pessoa confirma com senha e código MFA. Valor em centavos.`,
  keywords: [msg`aporte`, msg`SPI`, msg`conta PI`],
  permission: READ,
  input: spiTransactionInput,
  preview: (input) => {
    const amount = amountText(input);
    return {
      title: i18n._(msg`Solicitar aporte`),
      detail:
        amount === undefined
          ? t`Abre o formulário de aporte da conta corrente para a conta PI (APORTE CC>>GI). Pede senha e código MFA.`
          : t`Aporte de ${amount} da conta corrente para a conta PI (APORTE CC>>GI). Pede senha e código MFA.`,
    };
  },
  run: ({ nav, input }) => nav.search({ solicitacao: 'aporte', valor: input.amountCents === undefined ? undefined : String(input.amountCents) }),
});

/** Saque: move dinheiro da conta PI para a conta corrente ("SAQUE GI>>CC"). Mesmo step-up. */
export const spiWithdraw = action({
  id: 'spi.withdraw',
  kind: 'write',
  stepUp: true,
  label: msg`Saque`,
  description: msg`Prepara um saque, que traz dinheiro da conta PI de volta para a conta corrente do sistema de liquidação SPI (GI>>CC). Abre o formulário com o valor preenchido; depois a pessoa confirma com senha e código MFA. Valor em centavos.`,
  keywords: [msg`saque`, msg`SPI`, msg`conta PI`],
  permission: READ,
  input: spiTransactionInput,
  preview: (input) => {
    const amount = amountText(input);
    return {
      title: i18n._(msg`Solicitar saque`),
      detail:
        amount === undefined
          ? t`Abre o formulário de saque da conta PI para a conta corrente (SAQUE GI>>CC). Pede senha e código MFA.`
          : t`Saque de ${amount} da conta PI para a conta corrente (SAQUE GI>>CC). Pede senha e código MFA.`,
    };
  },
  run: ({ nav, input }) => nav.search({ solicitacao: 'saque', valor: input.amountCents === undefined ? undefined : String(input.amountCents) }),
});

export const spiPanelScreen = defineScreen({
  id: 'spi.panel',
  path: SPI_PATH,
  title: msg`Gestão SPI`,
  description: msg`Painel da Gestão SPI (liquidação do Pix): acompanhamento diário do saldo da conta corrente e do resumo da conta PI, com pedidos de aporte e de saque e o acesso à consulta de operações PIX.`,
  permission: READ,
  place: { icon: 'layout-dashboard', section: 'bank-account' },
  actions: [spiRefresh, spiOpenConsult, spiContribution, spiWithdraw],
});

/** O que o agente lê do painel (U24, U46 D2-C): no idioma da tela, só o estado; saldos arredondados a reais não cabem. */
export function spiPanelContext(input: {
  readonly loading: boolean;
  readonly open: string | undefined;
}): ScreenContext {
  const request = input.open;
  return {
    summary: [
      t`Painel da Gestão SPI.`,
      input.loading ? t`Saldos carregando.` : t`Saldos carregados.`,
      ...(request === undefined ? [] : [t`Formulário de solicitação aberto: ${request}.`]),
    ].join(' '),
    fields: { loading: input.loading, open: input.open ?? null },
    sensitivity: 'internal',
  };
}

// ---------------------------------------------------------------------------------------------
// Consulta · /contas-digitais/gestao-spi/consultas
// ---------------------------------------------------------------------------------------------

const optionalText = (max: number) => z.string().max(max).optional();
const centsText = z
  .string()
  .regex(/^\d{1,15}$/)
  .optional();
const LOCAL_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;
const localDateTime = () => z.string().regex(LOCAL_DATE_TIME);

/**
 * Os filtros do formulário (`SPIConsultForm.tsx`). Os quatro primeiros são obrigatórios no legado
 * (`SPIConsultSchema.ts:5-16`), e é por isso que a consulta só dispara depois do Pesquisar.
 */
export const spiConsultSearchInput = z.object({
  messageGroup: z.enum(['1', '2', '3', '4', '5', '6']).meta({ description: 'Message group: 1 PACS, 2 LPI, 3 all, 4 external, 5 internal, 6 PI account. Required.' }),
  operationType: z.enum(['1', '2', '3']).meta({ description: 'Sent/received: 1 credit, 2 debit, 3 all. Required.' }),
  startDate: localDateTime().meta({ description: 'Start of the period, wall clock, YYYY-MM-DDTHH:mm. Required.' }),
  endDate: localDateTime().meta({ description: 'End of the period, wall clock, YYYY-MM-DDTHH:mm; cannot be before startDate. Required.' }),
  amountFrom: centsText.meta({ description: 'Minimum amount in cents, digits only.' }),
  amountTo: centsText.meta({ description: 'Maximum amount in cents, digits only; cannot be lower than amountFrom.' }),
  counterpartyBankIspb: optionalText(20),
  counterpartyBankIspbDisplay: optionalText(20),
  bankName: optionalText(200),
  operationStatus: z.enum(['1', '2', '3', '4']).optional(),
  registrationNumberSearch: z
    .string()
    .regex(/^[A-Za-z0-9./\- ]{0,20}$/)
    .optional()
    .meta({ description: 'Payer/receiver CPF or CNPJ as typed; separators are stripped on the wire (letters are kept for alphanumeric CNPJ).' }),
  endToEndId: optionalText(200),
  errorMessageFilter: optionalText(200),
  entryChannel: optionalText(200),
  pixType: z.enum(['1', '2', '3', '4', '5', '6']).optional(),
});
export type SpiConsultSearchInput = z.infer<typeof spiConsultSearchInput>;

/** Patch da URL para uma consulta nova: troca todos os filtros aplicados, marca `consulta=1` e volta à página 1. */
export function spiConsultSearchPatch(input: Partial<SpiConsultSearchInput>, auxiliares?: string): Record<string, unknown> {
  const patch: Record<string, unknown> = Object.fromEntries(SPI_APPLIED_KEYS.map((key) => [key, undefined]));
  for (const key of [...SPI_FILTER_KEYS, 'counterpartyBankIspbDisplay', 'bankName'] as const) {
    const value = (input as Record<string, string | undefined>)[key];
    if (value !== undefined && value !== '') patch[key] = value;
  }
  if (auxiliares !== undefined && auxiliares !== '') patch['auxiliares'] = auxiliares;
  return { ...patch, consulta: 1, page: undefined };
}

export const spiConsultSearchAction = action({
  id: 'spi.consult.search',
  kind: 'read',
  label: msg`Pesquisar`,
  description: msg`Faz a consulta de operações PIX do SPI. messageGroup, operationType, startDate e endDate são obrigatórios; o resto é opcional. Valores em centavos. O resultado vem inteiro de uma vez e é paginado na tela.`,
  keywords: [msg`operações PIX`, msg`SPI`, msg`consulta`],
  permission: READ,
  input: spiConsultSearchInput,
  run: ({ nav, input }) => nav.search(spiConsultSearchPatch(input)),
});

export const spiConsultPage = listPageAction('spi.consult.page', { defaultSize: 5 });

export const spiExportXlsx = action({
  id: 'spi.consult.exportXlsx',
  kind: 'read',
  label: msg`Gerar XLS`,
  description: msg`Baixa o relatório XLSX da consulta de operações PIX que está na tela, com os mesmos filtros. Só leitura: não altera nada.`,
  permission: READ,
  run: () => {
    throw new Error('spi.consult.exportXlsx needs the open SPI query screen with the filters filled in');
  },
});

export const spiConsultScreen = defineScreen({
  id: 'spi.consult',
  path: SPI_CONSULT_PATH,
  title: msg`Consulta de operações PIX`,
  description: msg`Consulta de operações PIX do SPI: filtra as operações da conta PI por tipo de operação, crédito ou débito, período com data e hora, faixa de valor, banco da contraparte, status da mensagem, documento do pagador ou recebedor, id fim a fim, texto do erro, canal de entrada e tipo Pix, e exporta o resultado em XLSX.`,
  permission: READ,
  place: { icon: 'receipt-text', parent: 'spi.panel' },
  actions: [spiConsultSearchAction, spiConsultPage, spiExportXlsx],
});

/** O que o agente lê da consulta (U24, U46 D2-C): no idioma da tela, se já houve consulta, contagem e filtros ativos (só os nomes), sem dado pessoal. */
export function spiConsultContext(input: {
  readonly searched: boolean;
  readonly total: number | undefined;
  readonly filters: readonly string[];
}): ScreenContext {
  const total = input.total;
  const filterNames = input.filters.join(', ');
  const state = !input.searched ? t`Nenhuma consulta feita ainda.` : total === undefined ? t`Resultados carregando.` : t`${total} operações encontradas.`;
  return {
    summary: [t`Consulta de operações PIX do SPI.`, state, ...(input.filters.length > 0 ? [t`Filtros: ${filterNames}.`] : [])].join(' '),
    fields: { searched: input.searched, total: input.total ?? null },
    sensitivity: 'internal',
  };
}

/** Os filtros aplicados, só os nomes, para o contexto do agente (nunca os valores: há documento). */
export function spiActiveFilterNames(filters: SpiOperationFilters): string[] {
  return Object.entries(filters)
    .filter(([key, value]) => key !== 'auxiliares' && typeof value === 'string' && value !== '')
    .map(([key]) => key);
}
